import React, { useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { AlertTriangle, X } from "lucide-react";
import ProduitsForm from "./ProduitsForm";
import api from "../../../api";
import { Button } from "../../../components/ui/button";
import Input from "../../../components/ui/Input";

export default function ProduitsModal({
  produitSelectionne,
  setProduitSelectionne,
  openFicheProduit,
  setOpenFicheProduit,
  openEdition,
  setOpenEdition,
  fetchProduits,
  openDeleteModal,
  setOpenDeleteModal,
}) {
  const [confirmationText, setConfirmationText] = useState("");

  const handleClose = () => {
    setOpenFicheProduit(false);
    setOpenEdition(false);
    setOpenDeleteModal(false);
    setProduitSelectionne(null);
    setConfirmationText("");
  };

  const handleDelete = async () => {
    if (!produitSelectionne) return;
    
    try {
      await api.delete(`/produits/${produitSelectionne._id || produitSelectionne.id}`);
      fetchProduits();
      handleClose();
    } catch (error) {
      console.error("Erreur lors de la suppression du produit :", error);
    }
  }; 

  return (
    <>
      {/* Modal Voir Produit */}
      <Dialog open={openFicheProduit} onClose={handleClose} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-5xl bg-surface rounded-lg shadow-lg p-6">
            
            {/* En-tête */}
            <div className="flex justify-between items-center border-b border-[var(--hp-color-border-muted)] pb-3">
              <DialogTitle className="text-xl font-semibold text-primary">Fiche Produit</DialogTitle>
              <Button variant="ghost" size="icon" onClick={handleClose}>
                <X className="w-5 h-5 text-subtle hover:text-primary" />
              </Button>
            </div>

            {produitSelectionne ? (
              <>
                {/* Section : Informations Générales */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-4 border-b pb-4">
                  <p><strong>Nom :</strong> {produitSelectionne.nom_produit}</p>
                  <p><strong>Code ISIN :</strong> {produitSelectionne.code_isin}</p>
                  <p><strong>Société de Gestion :</strong> {produitSelectionne.societe_gestion}</p>
                  <p><strong>Site Web :</strong> <a href={produitSelectionne.site_web} className="text-secondary underline" target="_blank" rel="noopener noreferrer">{produitSelectionne.site_web}</a></p>
                  <p><strong>Type :</strong> {produitSelectionne.type}</p>
                  <p><strong>Dépositaire :</strong> {produitSelectionne.depositaire}</p>
                </div>

                {/* Section : Détails du produit */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-4 border-b pb-4">
                  <p><strong>Objectif :</strong> {produitSelectionne.objectif}</p>
                  <p><strong>Indice de Référence :</strong> {produitSelectionne.indice_reference}</p>
                  <p><strong>Risque :</strong> {produitSelectionne.risque}/7</p>
                  <p><strong>Durée Recommandée :</strong> {produitSelectionne.duree_recommandee}</p>
                  <p><strong>Description :</strong> {produitSelectionne.description}</p>
                  <p><strong>Commentaires :</strong> {produitSelectionne.commentaires}</p>
                  <p><strong>Lien DIC :</strong> <a href={produitSelectionne.lien_dic} className="text-secondary underline" target="_blank" rel="noopener noreferrer">{produitSelectionne.lien_dic}</a></p>
                </div>

                {/* Section : Stratégie d'investissement */}
                {produitSelectionne.strategie && (
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold border-b pb-2">Stratégie d'Investissement</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-4">
                      <p><strong>Allocation Taux :</strong> {produitSelectionne.strategie.allocation?.taux}</p>
                      <p><strong>Allocation Actions :</strong> {produitSelectionne.strategie.allocation?.actions}</p>
                      <p><strong>Allocation OPC :</strong> {produitSelectionne.strategie.allocation?.opc}</p>
                      <p><strong>Allocation Convertibles :</strong> {produitSelectionne.strategie.allocation?.convertibles}</p>
                      <p><strong>Dérivés :</strong> {produitSelectionne.strategie.derives}</p>
                      <p><strong>Sensibilité aux Taux :</strong> {produitSelectionne.strategie.sensibilite_taux}</p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p>Aucune information disponible.</p>
            )}

            {/* Boutons d'action */}
            <div className="flex justify-between mt-6 pt-4 border-t border-[var(--hp-color-border-muted)]">
              <Button variant="secondary" onClick={handleClose}>Fermer</Button>
              <div className="flex gap-3">
                <Button variant="primary" onClick={() => { setOpenFicheProduit(false); setOpenEdition(true); }}>
                  Modifier
                </Button>
                <Button variant="destructive" onClick={() => { setOpenFicheProduit(false); setOpenDeleteModal(true); }}>
                  Supprimer
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Modal Modifier Produit */}
      <ProduitsForm
        open={openEdition}
        setOpen={setOpenEdition}
        produitSelectionne={produitSelectionne}
        fetchProduits={fetchProduits}
      />

      {/* Modal de Confirmation Suppression */}
      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md bg-surface rounded-lg shadow-lg p-6">
            <div className="flex items-center gap-4">
              <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--hp-danger-50)] sm:mx-0 sm:h-10 sm:w-10">
                <AlertTriangle className="h-6 w-6 text-[var(--hp-danger-600)]" />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-lg font-semibold text-primary">Confirmer la suppression</DialogTitle>
                <p className="text-subtle mt-1 text-sm">Tapez <strong>"SUPPRIMER"</strong> pour confirmer :</p>
                <Input
                  type="text"
                  className="mt-2 w-full"
                  placeholder="SUPPRIMER"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setOpenDeleteModal(false)}>
                Annuler
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDelete} 
                disabled={confirmationText !== "SUPPRIMER"}
              >
                Supprimer
              </Button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}
